import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CommandService } from '@helios/commands'
import {
  ClearwaterTransaction,
  ClearwaterTransactionsFilterContext,
  ClearwaterTransactionsSetValues,
  ExternalDataSourceType,
  ExternalTransaction,
} from '@helios/common-api'
import {
  HeliosServerSideRequest,
  genericHeliosRequest,
} from '@helios/server-side-util'
import {
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Observable, of } from 'rxjs'
import { map, retry, take } from 'rxjs/operators'
import { TransactionAvailableRestrictedColumns } from '../models/clearwater-transaction'
import {
  ActionType,
  GetTransactionsResponse,
  TransactionActionRequest,
  TransactionsActionResponse,
  createTransactionAction,
} from '../models/clearwater-transactions-action.model'
import { CwTxSummaryResponse } from '../models/cw-tx-summary.model'
import { ExportExcelRequest } from '../models/export-to-excel.model'

export const TRANSACTIONS_URL = 'helios-cw-transactions'
export const EXPORT_URL = `${TRANSACTIONS_URL}/export`
export const ACTIONS_URL = `${TRANSACTIONS_URL}/actions`
export const TRANSACTIONS_SET_VALUES_URL = `${TRANSACTIONS_URL}/setValues`
export const TRANSACTIONS_GET_SUMMARIES_URL = `${TRANSACTIONS_URL}/summaries`
export const TRANSACTIONS_RESTRICTED_COLUMNS_URL = `${TRANSACTIONS_URL}/transactionRestrictedColumns`

const PANEL_NAME = 'Clearwater Transactions'

const RETRY_COUNT = 2

@Injectable({
  providedIn: 'root',
})
export class ClearwaterTransactionsService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly commandService: CommandService,
  ) {}

  /**
   * Set the transactions as "cleared" (updates the transaction, setting the
   * actualPostDate to match the expectedPostDate value)
   *
   * @internal
   */
  clear(
    transactions: ClearwaterTransaction[],
  ): Observable<TransactionsActionResponse> {
    const request = createTransactionAction(
      ActionType.clear,
      transactions.map(tx => tx.rowId),
    )
    return this.callActionsApi(request)
  }

  /**
   * Sets post date to the current session's recon date.
   */
  clearAndSetPostDate(
    transactions: ClearwaterTransaction[],
  ): Observable<TransactionsActionResponse> {
    const request = createTransactionAction(
      ActionType.clearAndSetPost,
      transactions.map(tx => tx.rowId),
    )
    return this.callActionsApi(request)
  }

  /**
   * Set transactions as "uncleared" (sets the actualPostDate to null)
   *
   * @internal
   */
  unclear(
    transactions: ClearwaterTransaction[],
  ): Observable<TransactionsActionResponse> {
    const request = createTransactionAction(
      ActionType.unclear,
      transactions.map(tx => tx.rowId),
    )
    return this.callActionsApi(request)
  }

  /**
   * Deletes transactions and all matched transactions.
   *
   * @param transactions to delete
   */
  delete(
    transactions: ClearwaterTransaction[],
  ): Observable<TransactionsActionResponse> {
    const request = createTransactionAction(
      ActionType.delete,
      transactions.map(tx => tx.rowId),
    )
    return this.callActionsApi(request)
  }

  /**
   * Deletes transaction and all matched transactions. Sets the transaction
   * to the ignored state.
   *
   * @param transactions to delete and ignore
   */
  deleteAndIgnore(
    transactions: ClearwaterTransaction[],
  ): Observable<TransactionsActionResponse> {
    const request = createTransactionAction(
      ActionType.deleteAndIgnore,
      transactions.map(tx => tx.rowId),
    )
    return this.callActionsApi(request)
  }

  async getTransactions(
    request: HeliosServerSideRequest<
      ClearwaterTransaction,
      ClearwaterTransactionsFilterContext
    >,
    cacheLoaded$: Observable<boolean> = of(true),
  ): Promise<GetTransactionsResponse> {
    const response = await this.httpClient
      .post<GetTransactionsResponse>(TRANSACTIONS_URL, request)
      .pipe(retry(RETRY_COUNT), take(1))
      .toPromise()

    const cacheLoaded = await cacheLoaded$.pipe(take(1)).toPromise()
    if (cacheLoaded) {
      return response
    }

    const newRowCount = response.data ? response.data.length : 0
    const totalRows = request.startRow + newRowCount
    if (totalRows < request.endRow) {
      await new Promise(resolve => setTimeout(() => resolve(null), 500))
      return this.getTransactions(request, cacheLoaded$)
    }

    return response
  }

  async getAllTransactionRowIds(
    request: HeliosServerSideRequest<
      ClearwaterTransaction,
      ClearwaterTransactionsFilterContext
    >,
  ): Promise<string[]> {
    const rowIds = await this.httpClient
      .post<GetTransactionsResponse>(
        TRANSACTIONS_URL,
        this.requestCwTxRowIds(request),
      )
      .pipe(map(response => response.data?.map(tx => tx.rowId) as string[]))
      .toPromise()

    return rowIds
  }

  requestCwTxRowIds(
    request: HeliosServerSideRequest<
      ClearwaterTransaction,
      ClearwaterTransactionsFilterContext
    >,
  ): HeliosServerSideRequest<
    ClearwaterTransaction,
    ClearwaterTransactionsFilterContext
  > {
    return {
      ...request,
      startRow: 0,
      endRow: 100001,
      fields: ['rowId'],
    }
  }

  match(
    transactions: ClearwaterTransaction[],
    externalTransactions: ExternalTransaction[],
  ): Observable<TransactionsActionResponse> {
    const request = createTransactionAction(
      ActionType.match,
      transactions.map(tx => tx.rowId),
      externalTransactions.map(tx => `${tx.extTranId}`),
    )
    return this.callActionsApi(request)
  }

  manyToManyMatch(
    transactions: ClearwaterTransaction[],
    externalTransactions: ExternalTransaction[],
  ): Observable<TransactionsActionResponse> {
    const request = createTransactionAction(
      ActionType.manyToManyMatch,
      transactions.map(tx => tx.rowId),
      externalTransactions.map(tx => `${tx.extTranId}`),
    )
    return this.callActionsApi(request)
  }

  matchUsingExternal(
    transactions: ClearwaterTransaction[],
    externalTransaction: ExternalTransaction,
  ): Observable<TransactionsActionResponse> {
    const request: TransactionActionRequest = createTransactionAction(
      ActionType.matchUsingExternal,
      transactions.map(tx => tx.rowId),
      [`${externalTransaction.extTranId}`],
    )

    return this.callActionsApi(request)
  }

  unmatch(
    transactions: ClearwaterTransaction[],
    dataSourceType?: ExternalDataSourceType,
  ): Observable<TransactionsActionResponse> {
    const request: TransactionActionRequest = createTransactionAction(
      ActionType.unmatch,
      transactions.map(tx => tx.rowId),
      [],
      dataSourceType,
    )
    return this.callActionsApi(request)
  }

  moveTransaction(
    cwTrans: ClearwaterTransaction[],
    destAccountId: number,
  ): Observable<TransactionsActionResponse> {
    const request: TransactionActionRequest = createTransactionAction(
      ActionType.moveTransaction,
      cwTrans.map(tx => tx.rowId),
      undefined,
      undefined,
      undefined,
      destAccountId,
    )
    return this.callActionsApi(request)
  }

  exportToExcel(request: ExportExcelRequest): Observable<Blob> {
    const options = {
      responseType: 'blob' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    const requestPayload = {
      ...genericHeliosRequest,
      headers: request.headers,
      fields: request.fields,
      context: request.context,
      filter: request.filters,
      userTimeZone: request.userTimeZone,
    }

    return this.httpClient.post(EXPORT_URL, requestPayload, options)
  }

  getSetValues(): Observable<ClearwaterTransactionsSetValues> {
    return this.httpClient.get<ClearwaterTransactionsSetValues>(
      TRANSACTIONS_SET_VALUES_URL,
    )
  }

  async getAllTxsSummary(
    request: HeliosServerSideRequest<
      ClearwaterTransaction,
      ClearwaterTransactionsFilterContext
    >,
  ): Promise<CwTxSummaryResponse> {
    const summaries = await this.httpClient
      .post<CwTxSummaryResponse>(TRANSACTIONS_GET_SUMMARIES_URL, request)
      .pipe(take(1))
      .toPromise()
    return summaries
  }

  private callActionsApi(
    request: TransactionActionRequest,
  ): Observable<TransactionsActionResponse> {
    return this.httpClient.post<TransactionsActionResponse>(
      ACTIONS_URL,
      request,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          request.actionType,
        ),
        context: new HttpContext()
          .set(HANDLE_ERROR_AND_THROW, true)
          .set(HELIOS_COMMAND_HTTP_TOKEN, true),
      },
    )
  }

  getAvailableRestrictedColumns(): Observable<
    TransactionAvailableRestrictedColumns[]
  > {
    return this.httpClient.get<TransactionAvailableRestrictedColumns[]>(
      TRANSACTIONS_RESTRICTED_COLUMNS_URL,
    )
  }
}
